/**
 * The bridge the Android shell injects, and safe answers for when it is absent.
 *
 * The shell hosts the live site in a WebView and exposes a `KiddoraApp` object on `window`. Every
 * capability behind it is native — Bazaar billing, SMS code auto-read, push — and none of it exists
 * in a browser, so every function here answers honestly rather than throwing. The site has to stay
 * fully usable in a plain browser: that is where a parent first arrives, and it is where the whole
 * product runs until an APK exists.
 *
 * **Presence is detected by a method, not by a user-agent string.** A WebView's UA can be anything
 * a manufacturer decided, and matching on it is how a feature ends up enabled on a device that
 * cannot do it. If the function is there, the capability is there.
 */

export interface AppShellBridge {
  /** Closes the app. The web side decides WHEN; only the shell can actually do it. */
  finishApp?: () => void;
  /** Whether the shell can read an incoming verification code for the parent. */
  isSmsOtpAvailable?: () => boolean;
  startSmsOtpListen?: () => void;
  stopSmsOtpListen?: () => void;
  /** Cafe Bazaar in-app billing — the reason a WebView shell exists rather than a TWA. */
  isBazaarBillingAvailable?: () => boolean;
  subscribeProduct?: (productId: string, payload: string) => void;
  restoreSubscriptions?: () => void;
  /** Tints the system bars to match the screen, so the app does not look pasted onto Android. */
  setChromeColors?: (backgroundColor: string, lightStatusBar: boolean) => void;
}

/**
 * One purchase receipt, exactly as Bazaar hands it to the shell.
 *
 * It is a CLAIM, not an entitlement. Nothing on this device may act on it beyond forwarding it to
 * the server, which asks Bazaar's developer API whether it is real.
 */
export interface BazaarPurchase {
  productId: string;
  purchaseToken: string;
  orderId?: string;
  payload?: string;
}

/**
 * What the shell reports back after a purchase or a restore.
 *
 * A discriminated union rather than an optional-fields bag, because every branch of the UI has to
 * handle a failure and a shape that merely might carry an error is a shape that gets read as
 * success. Cancellation arrives here too — it is an error status with `code: "canceled"` — since a
 * flow that can end silently leaves a spinner running forever.
 */
export type BazaarEvent =
  | { type: "subscribe"; status: "success"; purchase: BazaarPurchase }
  | { type: "restore"; status: "success"; products: BazaarPurchase[] }
  | { type: "subscribe" | "restore" | "connect"; status: "error"; code: string; detail?: string };

declare global {
  interface Window {
    KiddoraApp?: AppShellBridge;
  }
  interface WindowEventMap {
    /** Dispatched by the shell on the hardware back key. See `back-navigation.ts`. */
    "kiddora-system-back": Event;
    /** A verification code the shell read from an SMS, so a parent need not type it. */
    "kiddora-sms-code": CustomEvent<{ code: string }>;
    /** The outcome of a Bazaar purchase or restore. See `billing.ts`. */
    "kiddora-bazaar": CustomEvent<BazaarEvent>;
  }
}

const bridge = (): AppShellBridge | undefined =>
  typeof window === "undefined" ? undefined : window.KiddoraApp;

/** Running inside our Android shell. */
export function inAppShell(): boolean {
  try {
    return typeof bridge()?.finishApp === "function";
  } catch {
    return false;
  }
}

/**
 * The shell, or an installed PWA.
 *
 * Both are cases where back at the root should close the app rather than navigate away — in a
 * browser tab there is somewhere to go back TO, and taking that away would be wrong.
 */
export function inStandaloneApp(): boolean {
  if (typeof window === "undefined") return false;
  if (inAppShell()) return true;
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/** Closes the app if something can. A no-op in a browser, where closing a tab is not ours to do. */
export function finishApp(): void {
  try {
    bridge()?.finishApp?.();
  } catch {
    /* A shell that cannot close itself must not take the page down with it. */
  }
}

export function canAutoReadSmsCode(): boolean {
  try {
    return bridge()?.isSmsOtpAvailable?.() === true;
  } catch {
    return false;
  }
}

/**
 * Starts listening for the verification code, and returns the stop function.
 *
 * Android's consent API must be listening BEFORE the message arrives, so this is called as the code
 * is requested rather than when the input is focused.
 */
export function listenForSmsCode(onCode: (code: string) => void): () => void {
  if (!canAutoReadSmsCode()) return () => undefined;

  const handler = (event: WindowEventMap["kiddora-sms-code"]) => {
    const code = event.detail?.code;
    if (code) onCode(code);
  };
  window.addEventListener("kiddora-sms-code", handler);
  try {
    bridge()?.startSmsOtpListen?.();
  } catch {
    /* Listening is an optimisation; the parent can always type the code. */
  }

  return () => {
    window.removeEventListener("kiddora-sms-code", handler);
    try {
      bridge()?.stopSmsOtpListen?.();
    } catch {
      /* ignored */
    }
  };
}

/** Tints the Android system bars. Silently ignored in a browser. */
export function setSystemBarColors(background: string, lightStatusBar: boolean): void {
  try {
    bridge()?.setChromeColors?.(background, lightStatusBar);
  } catch {
    /* ignored */
  }
}

/**
 * Whether a subscription can be bought from here.
 *
 * False in every browser, and false in a shell built without Bazaar's RSA key. The site hides the
 * subscribe button rather than showing one that opens nothing — a payment button that does not
 * pay is worse than no payment button, because a parent tries it repeatedly.
 */
export function canBuySubscription(): boolean {
  try {
    return bridge()?.isBazaarBillingAvailable?.() === true;
  } catch {
    return false;
  }
}

/**
 * Subscribes to a product and resolves with the shell's answer.
 *
 * The bridge call is fire-and-forget and the result arrives as a window event, so this adapts the
 * two into one promise. Two details make that safe:
 *
 *   - The listener is attached BEFORE the flow starts. Attaching afterwards can miss an immediate
 *     failure — a missing Bazaar app rejects before the call returns.
 *   - It resolves rather than rejecting on a refusal. Cancelling is a normal thing a parent does,
 *     and modelling it as a thrown error means every caller needs a try/catch to handle the most
 *     ordinary outcome.
 */
function runBazaarFlow(
  start: () => void,
  wanted: "subscribe" | "restore",
): Promise<BazaarEvent> {
  return new Promise((resolve) => {
    const handler = (event: WindowEventMap["kiddora-bazaar"]) => {
      const detail = event.detail;
      // A connection failure has its own type and belongs to whichever flow is waiting: nothing
      // else can be, since the UI only ever runs one at a time.
      if (detail.type !== wanted && detail.type !== "connect") return;
      window.removeEventListener("kiddora-bazaar", handler);
      resolve(detail);
    };
    window.addEventListener("kiddora-bazaar", handler);

    try {
      start();
    } catch (err) {
      window.removeEventListener("kiddora-bazaar", handler);
      resolve({
        type: wanted,
        status: "error",
        code: "bridge_failed",
        detail: err instanceof Error ? err.message : String(err),
      });
    }
  });
}

export function subscribeViaBazaar(productId: string, payload: string): Promise<BazaarEvent> {
  if (!canBuySubscription()) {
    return Promise.resolve({ type: "subscribe", status: "error", code: "unavailable" });
  }
  return runBazaarFlow(() => bridge()?.subscribeProduct?.(productId, payload), "subscribe");
}

export function restoreViaBazaar(): Promise<BazaarEvent> {
  if (!canBuySubscription()) {
    return Promise.resolve({ type: "restore", status: "error", code: "unavailable" });
  }
  return runBazaarFlow(() => bridge()?.restoreSubscriptions?.(), "restore");
}
